// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useEffect, useRef, useState } from 'react';
import { useStableCallback } from '@cloudscape-design/component-toolkit/internal';
import { getOwnerSelection } from '../core/caret-controller';
import { normalizeCaretIntoTrigger } from '../core/dom-utils';
import { isTriggerToken } from '../core/type-guards';
export function useShortcutsState() {
    const [caretInTrigger, setCaretInTrigger] = useState(null);
    const triggerElementsRef = useRef(new Map());
    const cancelledTriggerIds = useRef(new Set());
    const menuJustClosed = useRef(null);
    const lastEmittedTokensRef = useRef(undefined);
    const markTokensAsSent = useStableCallback((tokens) => {
        lastEmittedTokensRef.current = tokens;
    });
    return {
        caretInTrigger,
        setCaretInTrigger,
        triggerElementsRef,
        cancelledTriggerIds,
        menuJustClosed,
        lastEmittedTokensRef,
        markTokensAsSent,
    };
}
/**
 * Manages trigger menu state by listening for selection changes within the
 * editable element. Determines which trigger (if any) the caret is inside
 * and updates `caretInTrigger` accordingly.
 *
 * Supports menu suppression (via Escape) which is cleared on the next user
 * interaction that changes the selection.
 */
export function useShortcutsEffects(config) {
    const { activeTriggerToken, editableElementRef, state, tokens, caretController } = config;
    useEffect(() => {
        var _a;
        const hasTriggers = tokens === null || tokens === void 0 ? void 0 : tokens.some(isTriggerToken);
        if (!hasTriggers || !editableElementRef.current) {
            state.setCaretInTrigger(null);
            return;
        }
        const checkMenuState = () => {
            const ctrl = caretController.current;
            if (!editableElementRef.current || !ctrl) {
                return;
            }
            // Only react to selection changes within this input
            const sel = getOwnerSelection(editableElementRef.current);
            if (!(sel === null || sel === void 0 ? void 0 : sel.rangeCount) || !editableElementRef.current.contains(sel.getRangeAt(0).startContainer)) {
                return;
            }
            normalizeCaretIntoTrigger(editableElementRef.current, state.cancelledTriggerIds.current);
            const activeTrigger = ctrl.findActiveTrigger();
            // The caret can land inside a cancelled trigger's DOM element (e.g. via
            // arrow keys). Without this guard, setCaretInTrigger would reopen the menu.
            const activeTriggerId = activeTrigger && !state.cancelledTriggerIds.current.has(activeTrigger.id) ? activeTrigger.id : null;
            // Skip reopening the trigger that was just closed via Escape.
            // Clears after one check so subsequent actions can reopen it.
            if (state.menuJustClosed.current) {
                const closedId = state.menuJustClosed.current;
                state.menuJustClosed.current = null;
                if (activeTriggerId === closedId) {
                    return;
                }
            }
            if (activeTriggerId !== state.caretInTrigger) {
                state.setCaretInTrigger(activeTriggerId);
            }
        };
        const onKeyUp = (e) => {
            if (e.key === 'Escape') {
                return;
            }
            checkMenuState();
        };
        const ownerDoc = (_a = editableElementRef.current.ownerDocument) !== null && _a !== void 0 ? _a : document;
        const element = editableElementRef.current;
        checkMenuState();
        ownerDoc.addEventListener('selectionchange', checkMenuState);
        element.addEventListener('keyup', onKeyUp);
        return () => {
            ownerDoc.removeEventListener('selectionchange', checkMenuState);
            element.removeEventListener('keyup', onKeyUp);
        };
    }, [tokens, state, editableElementRef, caretController, activeTriggerToken]);
}
//# sourceMappingURL=use-shortcuts.js.map